import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { APP_CONSTANTS } from '@constants';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { RoutePaths } from '@routes';

/**
 * Clears all authentication state (cookies + storage)
 */
function clearAuthState(authService: AuthenticationService) {
  try {
    authService.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.AUTH_TOKEN);
    authService.deleteCookie(APP_CONSTANTS.COOKIE_NAMES.USER);
    localStorage.clear();
    sessionStorage.clear();
  } catch (err) {
    console.error('Error clearing auth state:', err);
  }
}

/**
 * Route guard for authenticated-only pages.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  try {
    const isAuthenticated = authService.isAuthenticated();

    if (isAuthenticated) {
      const userData = authService.getUserData();
      if (userData) return true;

      console.warn('Auth token exists but user data missing. Clearing state.');
      clearAuthState(authService);
    }

    // Redirect to login with returnUrl
    const returnUrl = state.url !== '/' ? state.url : undefined;
    const loginUrl = RoutePaths.LOGIN;
    router.navigate([loginUrl], {
      queryParams: returnUrl ? { returnUrl } : {},
    });

    return false;
  } catch (error) {
    console.error('Auth guard error:', error);
    clearAuthState(authService);
    router.navigateByUrl(RoutePaths.LOGIN);
    return false;
  }
};

/**
 * Route guard for guest-only pages (login, register, etc.).
 */
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);

  try {
    const isAuthenticated = authService.isAuthenticated();
    const user = authService.getUserData();

    if (isAuthenticated && user) {
      // ✅ Safe access with null check
      router.navigateByUrl(
        user.role === 'admin' ? RoutePaths.USERS : RoutePaths.PROMPT
      );
      return false;
    }

    return true;
  } catch (error) {
    console.error('Guest guard error:', error);
    return true; // allow guest pages on error
  }
};
